/**
 * Componente ProgressBar (Barra de progreso)
 * Fecha: 16 Oct 2025
 */

import React from 'react';

export type ProgressVariant = 'default' | 'success' | 'warning' | 'error' | 'purple';
export type ProgressSize = 'sm' | 'md' | 'lg';

interface ProgressBarProps {
  /** Porcentaje de progreso (0-100) */
  value: number;
  /** Máximo valor (default: 100) */
  max?: number;
  /** Variante que define el color */
  variant?: ProgressVariant;
  /** Tamaño de la barra */
  size?: ProgressSize;
  /** Mostrar el porcentaje como texto */
  showLabel?: boolean;
  /** Texto personalizado en lugar del porcentaje */
  label?: string;
  /** Clases adicionales */
  className?: string;
  textClassName?: string;
}

const variantClasses: Record<ProgressVariant, string> = {
  default: 'bg-[#1FB7EE]',
  success: 'bg-[#1FD361]',
  warning: 'bg-warning',
  // `bg-red` no existe en Tailwind —las de color piden tono— así que la barra
  // salía sin fondo. Es la única variante que nadie usa todavía.
  error: 'bg-red-500',
  purple: 'bg-indigo-500'
};

/** A partir de aquí el avance se considera completo y la barra va en verde. */
const UMBRAL_COMPLETO = 70;

const sizeClasses: Record<ProgressSize, string> = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
};

/**
 * Barra de progreso lineal
 * 
 * @example
 * <ProgressBar value={75} showLabel />
 * <ProgressBar value={50} variant="warning" />
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  variant = 'default',
  size = 'md',
  showLabel = false,
  label,
  className = '',
  textClassName = ''
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  /**
   * Sin variante explícita, el color lo decide el avance: verde al llegar al
   * 70%, azul mientras falta.
   *
   * Antes eran cuatro ramas de las que solo corrían dos: `percentage < 80`
   * atrapaba todo lo que no fuera `>= 80`, así que las de `< 40` y el `else`
   * eran inalcanzables y cualquier avance por debajo de 80 salía ámbar.
   */
  const autoVariant: ProgressVariant =
    variant === "default"
      ? percentage >= UMBRAL_COMPLETO
        ? "success"
        : "default"
      : variant;

  return (
      <div className={`flex items-center gap-2 w-full ${className}`}>
      {showLabel && (
        <div className="flex justify-between items-center mb-1">
          <span className={`text-sm font-medium text-gray-700 dark:text-gray-300 ${textClassName}`}>
            {label || `${Math.round(percentage)}%`}
          </span>
        </div>
      )}
      <div className="w-full bg-[#E9ECEF] rounded-full overflow-hidden">
        <div
          className={`${sizeClasses[size]} ${variantClasses[autoVariant]} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
        />
      </div>
    </div>
  );
};

interface CircularProgressProps {
  /** Porcentaje de progreso (0-100) */
  value: number;
  /** Tamaño del círculo en píxeles */
  size?: number;
  /** Grosor del trazo */
  strokeWidth?: number;
  /** Color del progreso */
  color?: string;
  /** Mostrar el porcentaje en el centro */
  showLabel?: boolean;
  /** Texto personalizado */
  label?: string;
  /** Clases adicionales */
  className?: string;
}

/**
 * Barra de progreso circular
 * 
 * @example
 * <CircularProgress value={85} showLabel />
 * <CircularProgress value={50} color="#ef4444" size={120} />
 */
export const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  size = 120,
  strokeWidth = 8,
  color,
  showLabel = true,
  label,
  className = '',
}) => {
  const percentage = Math.min(Math.max(value, 0), 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  // Determinar color automáticamente basado en el porcentaje
  let progressColor = color;
  if (!progressColor) {
    if (percentage >= 100) {
      progressColor = '#10b981'; // verde
    } else if (percentage >= 80) {
      progressColor = '#f59e0b'; // amarillo
    } else {
      progressColor = '#ef4444'; // rojo
    }
  }

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-gray-200 dark:text-gray-700"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
      </svg>
      {showLabel && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-gray-800 dark:text-gray-200">
            {label || `${Math.round(percentage)}%`}
          </span>
        </div>
      )}
    </div>
  );
};
